import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * Round 63 — Settings → Schedule tab.
 *
 * <p>Operator-managed cron schedules. Currently one widget — "Tor bridges" —
 * covers two task types:
 *   • tor_bridge_source_fetch — periodic Source fetch (one row per source)
 *   • tor_bridge_batch_check  — periodic Batch check (one row per bridge kind)
 *
 * <p>UI follows the reference design: card header with counter chips (Total /
 * Active) + "+ Add Job" button; table with Name · Type · Schedule · Status ·
 * Created · Last Run · Actions; modal-driven Create / Edit (no inline form).
 */
export function ScheduleTab() {
  return (
    <div className="space-y-6">
      <TorBridgesScheduleWidget />
    </div>
  );
}

const TASK_TYPES = Object.freeze({
  tor_bridge_source_fetch:     { label: 'Source fetch',           icon: 'fa-cloud-arrow-down', category: 'tor_bridges' },
  tor_bridge_batch_check:      { label: 'Batch check',            icon: 'fa-stethoscope',      category: 'tor_bridges' },
  // 2026-06-22 — periodic cleanup of revoked terminal-registry rows
  // older than `olderThanDays`. Handler in services/scheduled-tasks.js
  // calls services/terminals-registry.js#sweepRevokedOlderThan.
  terminals_registry_cleanup:  { label: 'Cleanup revoked terminals', icon: 'fa-broom',         category: 'maintenance' },
  // 2026-07-05 — Round 1.450: promote active rows whose cert_not_after
  // has passed → status='expired'. No task_params — the sweep is just
  // a comparison against now(). Nodes pick up the new state via
  // policy snapshot push and blackout the terminal until admin renews
  // through Personnel.
  terminals_registry_expiry_sweep: { label: 'Terminal cert expiry sweep', icon: 'fa-hourglass-end', category: 'maintenance' },
});

/** Cron presets — operator-friendly templates. Custom is always allowed. */
const CRON_PRESETS = Object.freeze([
  { id: 'every_hour',     label: 'Every hour',       expr: '0 * * * *' },
  { id: 'every_3_hours',  label: 'Every 3 hours',    expr: '0 */3 * * *' },
  { id: 'every_6_hours',  label: 'Every 6 hours',    expr: '0 */6 * * *' },
  { id: 'daily_3am',      label: 'Daily 03:00',      expr: '0 3 * * *' },
  { id: 'weekly_mon',     label: 'Weekly Mon 03:00', expr: '0 3 * * 1' },
]);

const BRIDGE_KINDS = Object.freeze([
  { id: 'obfs4',     label: 'obfs4'     },
  { id: 'snowflake', label: 'snowflake' },
  { id: 'meek_lite', label: 'meek_lite' },
  { id: 'webtunnel', label: 'webtunnel' },
  { id: 'vanilla',   label: 'vanilla'   },
]);

/** Tailwind palette per bridge kind — visual cue in the Type chip. */
function bridgeKindPalette(kind) {
  switch (kind) {
    case 'obfs4':     return 'bg-blue-50 text-blue-700 ring-blue-200';
    case 'snowflake': return 'bg-cyan-50 text-cyan-700 ring-cyan-200';
    case 'meek_lite': return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'webtunnel': return 'bg-rose-50 text-rose-700 ring-rose-200';
    case 'vanilla':   return 'bg-slate-100 text-slate-700 ring-slate-200';
    default:          return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  }
}

/** 2026-06-22 — palette for non-bridge task chips. Slate / muted so
 *  Tor bridge rows visually stand out as the dominant category. */
const MAINTENANCE_CHIP = 'bg-slate-100 text-slate-700 ring-slate-200';

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

function TorBridgesScheduleWidget() {
  const toast = useToast();
  const [tasks, setTasks]     = useState(null);   // null = loading
  const [sources, setSources] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [running, setRunning] = useState(() => new Set());
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [editing, setEditing] = useState(null);   // 'new' | task object | null

  async function refresh() {
    try {
      const [tRes, sRes] = await Promise.all([
        // 2026-06-22 — load tasks across ALL categories so the new
        // `maintenance` cleanup task appears in the same widget. The
        // server `category=` filter is optional; omitting it returns
        // every category.
        api.scheduledTasks.list(),
        api.torBridgeSources.list().catch(() => ({ sources: [] })),
      ]);
      setTasks(tRes?.tasks || []);
      setSources(sRes?.sources || []);
      setLoadError(null);
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Failed to load scheduled tasks.';
      setLoadError(msg);
    }
  }
  useEffect(() => { refresh(); }, []);

  const total  = tasks ? tasks.length : 0;
  const active = tasks ? tasks.filter((t) => t.enabled).length : 0;

  function markBusy(id, on) {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  async function runNow(task) {
    setRunning((prev) => new Set(prev).add(task.id));
    try {
      const out = await api.scheduledTasks.run(task.id);
      const status = out?.result?.status;
      if (status === 'ok') {
        toast.success(`"${task.name}" ran successfully.`);
      } else {
        toast.error(`"${task.name}" failed: ${out?.result?.errorMessage || 'see audit log'}`);
      }
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Run failed.';
      toast.error(msg);
    } finally {
      setRunning((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
      refresh();
    }
  }

  async function toggleEnabled(task) {
    markBusy(task.id, true);
    try {
      await api.scheduledTasks.update(task.id, { enabled: !task.enabled });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Update failed.';
      toast.error(msg);
    } finally {
      markBusy(task.id, false);
    }
  }

  async function remove(task) {
    if (!window.confirm(`Delete scheduled task "${task.name}"?`)) return;
    markBusy(task.id, true);
    try {
      await api.scheduledTasks.remove(task.id);
      toast.success(`"${task.name}" deleted.`);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Delete failed.';
      toast.error(msg);
    } finally {
      markBusy(task.id, false);
    }
  }

  async function saveTask({ id, payload }) {
    try {
      if (id == null) {
        // Category is derived from task type — single source of truth in
        // TASK_TYPES. Falls back to tor_bridges to keep older callers safe.
        const category = TASK_TYPES[payload.taskType]?.category || 'tor_bridges';
        await api.scheduledTasks.create({ category, ...payload });
        toast.success(`"${payload.name}" created.`);
      } else {
        await api.scheduledTasks.update(id, payload);
        toast.success(`"${payload.name}" updated.`);
      }
      setEditing(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.payload?.error || err.message : 'Save failed.';
      throw new Error(msg);    // bubble up so the modal can render it inline
    }
  }

  return (
    <section className="rounded-2xl border border-line bg-surface shadow-card">
      {/* ─────── Header ─────── */}
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-ink">Cron Jobs</h2>
          <p className="mt-0.5 text-xs text-mist">
            Manage scheduled tasks
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CounterChip color="blue"    icon="fa-layer-group"  label="Total"  value={total} />
          <CounterChip color="emerald" icon="fa-circle-check" label="Active" value={active} />
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => setEditing('new')}
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
            Add Job
          </button>
        </div>
      </header>

      {/* ─────── Body ─────── */}
      {loadError ? (
        <div className="px-5 py-8 text-center text-sm text-rose-700">{loadError}</div>
      ) : tasks == null ? (
        <div className="px-5 py-12 text-center text-sm text-mist">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
          Loading…
        </div>
      ) : tasks.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className="fa-solid fa-clock text-lg" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-ink">No scheduled tasks yet</p>
          <p className="mt-1 text-xs text-mist">
            Click <span className="font-medium">+ Add Job</span> to create a periodic Source fetch or Batch check.
          </p>
        </div>
      ) : (
        <JobsTable
          tasks={tasks}
          sources={sources}
          busyIds={busyIds}
          running={running}
          onRun={runNow}
          onToggle={toggleEnabled}
          onEdit={(t) => setEditing(t)}
          onDelete={remove}
        />
      )}

      {/* ─────── Modal ─────── */}
      {editing && (
        <JobModal
          initial={editing === 'new' ? null : editing}
          sources={sources}
          onClose={() => setEditing(null)}
          onSave={saveTask}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header helpers
// ---------------------------------------------------------------------------

function CounterChip({ color, icon, label, value }) {
  const palette = color === 'emerald'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : 'bg-blue-50 text-blue-700 ring-blue-200';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold ring-1 ${palette}`}
    >
      <i className={`fa-solid ${icon} text-[11px]`} aria-hidden="true" />
      {label}: {value}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function JobsTable({ tasks, sources, busyIds, running, onRun, onToggle, onEdit, onDelete }) {
  const sourcesById = useMemo(() => {
    const m = new Map();
    for (const s of sources) m.set(s.id, s);
    return m;
  }, [sources]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-mist">
          <tr className="border-b border-line">
            <th className="px-5 py-3 text-left font-medium">Name</th>
            <th className="px-3 py-3 text-left font-medium">Type</th>
            <th className="px-3 py-3 text-left font-medium">Schedule</th>
            <th className="px-3 py-3 text-left font-medium">Status</th>
            <th className="px-3 py-3 text-left font-medium">Created</th>
            <th className="px-3 py-3 text-left font-medium">Last Run</th>
            <th className="px-5 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {tasks.map((t) => (
            <JobRow
              key={t.id}
              task={t}
              sourcesById={sourcesById}
              busy={busyIds.has(t.id)}
              running={running.has(t.id)}
              onRun={() => onRun(t)}
              onToggle={() => onToggle(t)}
              onEdit={() => onEdit(t)}
              onDelete={() => onDelete(t)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobRow({ task, sourcesById, busy, running, onRun, onToggle, onEdit, onDelete }) {
  return (
    <tr className="hover:bg-paper/50">
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <i className="fa-solid fa-clock-rotate-left text-[13px] text-mist" aria-hidden="true" />
          <span className="font-medium text-ink">{task.name}</span>
        </div>
      </td>
      <td className="px-3 py-3.5">
        <TypeChip task={task} sourcesById={sourcesById} />
      </td>
      <td className="px-3 py-3.5">
        <code className="rounded bg-paper px-1.5 py-0.5 font-mono text-[12px] text-ink/80">
          {task.cronExpr}
        </code>
      </td>
      <td className="px-3 py-3.5">
        <StatusChip enabled={task.enabled} />
      </td>
      <td className="px-3 py-3.5 text-mist">{formatDate(task.createdAt)}</td>
      <td className="px-3 py-3.5"><LastRunCell task={task} /></td>
      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-1">
          <IconBtn
            title="Run now"
            icon={running ? 'fa-spinner fa-spin' : 'fa-play'}
            tone="blue"
            disabled={running || busy}
            onClick={onRun}
          />
          <IconBtn
            title={task.enabled ? 'Deactivate' : 'Activate'}
            icon={task.enabled ? 'fa-toggle-on' : 'fa-toggle-off'}
            tone={task.enabled ? 'emerald' : 'slate'}
            size="lg"
            disabled={busy}
            onClick={onToggle}
          />
          <IconBtn title="Edit"   icon="fa-pen"   tone="slate" disabled={busy} onClick={onEdit} />
          <IconBtn title="Delete" icon="fa-trash" tone="rose"  disabled={busy} onClick={onDelete} />
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Chips & cells
// ---------------------------------------------------------------------------

function TypeChip({ task, sourcesById }) {
  const meta   = TASK_TYPES[task.taskType] || { label: task.taskType, icon: 'fa-circle' };
  const params = task.taskParams || {};

  let palette = 'bg-blue-50 text-blue-700 ring-blue-200';
  let detail  = '';

  if (task.taskType === 'tor_bridge_source_fetch') {
    const sid = Number(params.sourceId);
    const src = sourcesById.get(sid);
    detail = src ? (src.name || src.url || `#${sid}`) : `#${sid || '?'}`;
    palette = 'bg-blue-50 text-blue-700 ring-blue-200';
  } else if (task.taskType === 'tor_bridge_batch_check') {
    const kind = String(params.kind || '');
    detail = kind || '—';
    palette = bridgeKindPalette(kind);
  } else if (task.taskType === 'terminals_registry_cleanup') {
    const d = Math.floor(Number(params.olderThanDays) || 0);
    detail = d > 0 ? `> ${d} days` : '—';
    palette = MAINTENANCE_CHIP;
  } else if (task.taskType === 'terminals_registry_expiry_sweep') {
    // 2026-07-05 — no params. Chip shows just the label.
    palette = MAINTENANCE_CHIP;
  }

  return (
    <span
      className={`inline-flex max-w-[260px] items-center gap-1.5 rounded-lg px-2 py-1 text-[11.5px] font-medium ring-1 ${palette}`}
      title={`${meta.label}${detail ? ' · ' + detail : ''}`}
    >
      <i className={`fa-solid ${meta.icon} text-[10px]`} aria-hidden="true" />
      <span className="truncate">
        {meta.label}
        {detail && <span className="opacity-70"> · {detail}</span>}
      </span>
    </span>
  );
}

function StatusChip({ enabled }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1 text-[11.5px] font-medium text-emerald-700 ring-1 ring-emerald-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[11.5px] font-medium text-slate-600 ring-1 ring-slate-200">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      Paused
    </span>
  );
}

function LastRunCell({ task }) {
  if (!task.lastRunAt) return <span className="text-mist">—</span>;
  const ok = task.lastStatus === 'ok';
  const msg = ok ? 'Success' : (task.lastStatus || 'error');
  return (
    <span className="inline-flex items-center gap-1.5 text-mist">
      <span>{formatDateTime(task.lastRunAt)}</span>
      {ok ? (
        <i className="fa-solid fa-circle-check text-[12px] text-emerald-600" title={msg} aria-hidden="true" />
      ) : (
        <i className="fa-solid fa-circle-xmark text-[12px] text-rose-600" title={msg} aria-hidden="true" />
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Iconic action button (square 32×32)
// ---------------------------------------------------------------------------

function IconBtn({ title, icon, tone, size, disabled, onClick }) {
  const toneCls = {
    blue:    'text-blue-600 hover:bg-blue-50',
    emerald: 'text-emerald-600 hover:bg-emerald-50',
    slate:   'text-slate-500 hover:bg-slate-100',
    rose:    'text-rose-500 hover:bg-rose-50',
  }[tone] || 'text-slate-500 hover:bg-slate-100';
  const iconCls = size === 'lg' ? 'text-[18px]' : 'text-[13px]';
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${toneCls} ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <i className={`fa-solid ${icon} ${iconCls}`} aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal — Create / Edit cron job
// ---------------------------------------------------------------------------

function JobModal({ initial, sources, onClose, onSave }) {
  const isEdit = !!initial;
  const [name, setName]         = useState(initial?.name || '');
  const [taskType, setTaskType] = useState(initial?.taskType || 'tor_bridge_batch_check');
  const [sourceId, setSourceId] = useState(
    initial?.taskParams?.sourceId != null ? String(initial.taskParams.sourceId) : (sources[0]?.id ? String(sources[0].id) : ''),
  );
  const [kind, setKind] = useState(initial?.taskParams?.kind || 'obfs4');
  // 2026-06-22 — task param for terminals_registry_cleanup. Default
  // 90 days. Operator-tunable in this modal.
  const [olderThanDays, setOlderThanDays] = useState(
    initial?.taskParams?.olderThanDays != null ? String(initial.taskParams.olderThanDays) : '90',
  );
  const [scheduleMode, setScheduleMode] = useState(() => {
    const m = CRON_PRESETS.find((p) => p.expr === initial?.cronExpr);
    return m ? m.id : (initial ? 'custom' : 'every_6_hours');
  });
  const [cronExpr, setCronExpr] = useState(initial?.cronExpr || '0 */6 * * *');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState(null);

  // ESC closes
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !saving) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  function applyScheduleMode(mode) {
    setScheduleMode(mode);
    if (mode !== 'custom') {
      const p = CRON_PRESETS.find((x) => x.id === mode);
      if (p) setCronExpr(p.expr);
    }
  }

  function onChangeCron(v) {
    setCronExpr(v);
    setScheduleMode('custom');
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);

    const trimmedName = name.trim();
    if (!trimmedName) { setErr('Name is required.'); return; }
    if (!cronExpr.trim()) { setErr('Cron expression is required.'); return; }

    let taskParams;
    if (taskType === 'tor_bridge_source_fetch') {
      const sid = Number(sourceId);
      if (!Number.isInteger(sid) || sid <= 0) { setErr('Pick a source.'); return; }
      taskParams = { sourceId: sid };
    } else if (taskType === 'tor_bridge_batch_check') {
      const k = String(kind || '').trim();
      if (!k) { setErr('Pick a bridge kind.'); return; }
      taskParams = { kind: k };
    } else if (taskType === 'terminals_registry_cleanup') {
      const d = Math.floor(Number(olderThanDays));
      if (!Number.isFinite(d) || d < 1) { setErr('"Older than days" must be ≥ 1.'); return; }
      if (d > 3650) { setErr('"Older than days" too large (max 3650).'); return; }
      taskParams = { olderThanDays: d };
    } else if (taskType === 'terminals_registry_expiry_sweep') {
      // 2026-07-05 — Round 1.450: no params required.
      taskParams = {};
    } else {
      setErr(`Unknown task type: ${taskType}`);
      return;
    }

    const payload = {
      name:     trimmedName,
      taskType,
      taskParams,
      cronExpr: cronExpr.trim(),
    };

    setSaving(true);
    try {
      await onSave({ id: initial?.id ?? null, payload });
      // parent closes the modal on success
    } catch (e2) {
      setErr(e2?.message || 'Save failed.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-line"
      >
        <header className="mb-5 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <i className={`fa-solid ${isEdit ? 'fa-pen' : 'fa-clock'}`} aria-hidden="true" />
          </div>
          <h2 className="text-base font-semibold text-ink">
            {isEdit ? 'Edit Cron Job' : 'New Cron Job'}
          </h2>
        </header>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="cj_name" className="field-label">Name</label>
            <input
              id="cj_name"
              type="text"
              className="field"
              placeholder="e.g. Hourly snowflake probe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              maxLength={120}
              autoFocus
            />
          </div>

          {/* Task type */}
          <div>
            <label htmlFor="cj_type" className="field-label">Task type</label>
            <select
              id="cj_type"
              className="field"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              disabled={saving || isEdit}
            >
              <option value="tor_bridge_batch_check">Batch check (Tor bridges)</option>
              <option value="tor_bridge_source_fetch">Source fetch (Tor bridges)</option>
              <option value="terminals_registry_cleanup">Cleanup revoked terminals (Maintenance)</option>
            </select>
            {isEdit && (
              <p className="mt-1 text-[11px] text-mist">
                Task type can't be changed after creation — delete and re-add to switch.
              </p>
            )}
          </div>

          {/* Param select */}
          {taskType === 'tor_bridge_source_fetch' ? (
            <div>
              <label htmlFor="cj_src" className="field-label">Source</label>
              <select
                id="cj_src"
                className="field"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                disabled={saving || sources.length === 0}
              >
                {sources.length === 0
                  ? <option value="">— no sources defined —</option>
                  : (
                    <>
                      <option value="">— pick a source —</option>
                      {sources.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name || s.url}
                        </option>
                      ))}
                    </>
                  )}
              </select>
              {sources.length === 0 && (
                <p className="mt-1 text-[11px] text-mist">
                  Add a source first via Settings → Tor bridges → Add bridges.
                </p>
              )}
            </div>
          ) : taskType === 'tor_bridge_batch_check' ? (
            <div>
              <label htmlFor="cj_kind" className="field-label">Bridge kind</label>
              <select
                id="cj_kind"
                className="field"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                disabled={saving}
              >
                {BRIDGE_KINDS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            </div>
          ) : taskType === 'terminals_registry_cleanup' ? (
            <div>
              <label htmlFor="cj_days" className="field-label">Older than (days)</label>
              <input
                id="cj_days"
                type="number"
                min="1"
                max="3650"
                step="1"
                className="field"
                value={olderThanDays}
                onChange={(e) => setOlderThanDays(e.target.value)}
                disabled={saving}
              />
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-mist">
                <i className="fa-regular fa-circle-info" aria-hidden="true" />
                Revoked terminals older than this are physically deleted from the registry.
              </p>
            </div>
          ) : taskType === 'terminals_registry_expiry_sweep' ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800 ring-1 ring-amber-200">
              <i className="fa-regular fa-circle-info mr-1.5" aria-hidden="true" />
              Compares each active terminal cert's <code>notAfter</code> against now
              and promotes past-due rows to <strong>expired</strong>. No parameters.
              Recommended cadence: hourly or every 15 minutes.
            </p>
          ) : null}

          {/* Schedule */}
          <div>
            <label htmlFor="cj_sched" className="field-label">Schedule</label>
            <select
              id="cj_sched"
              className="field"
              value={scheduleMode}
              onChange={(e) => applyScheduleMode(e.target.value)}
              disabled={saving}
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
            <input
              type="text"
              className="field mt-2 font-mono text-[12px]"
              placeholder="Cron expression: * * * * *"
              value={cronExpr}
              onChange={(e) => onChangeCron(e.target.value)}
              disabled={saving}
              maxLength={120}
            />
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-mist">
              <i className="fa-regular fa-circle-info" aria-hidden="true" />
              Format: minute hour day month weekday
            </p>
          </div>

          {err && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {err}
            </div>
          )}
        </div>

        <footer className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary inline-flex items-center gap-2"
            disabled={saving}
          >
            <i className={`fa-solid ${isEdit ? 'fa-floppy-disk' : 'fa-plus'}`} aria-hidden="true" />
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save' : 'Create')}
          </button>
        </footer>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Format helpers — match the reference look ("14 Apr 2026" / "30 May 2026, 03:03:49 EEST").
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString('en-GB', {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    });
    const timePart = d.toLocaleTimeString('en-GB', {
      hour:         '2-digit',
      minute:       '2-digit',
      second:       '2-digit',
      hour12:       false,
      timeZoneName: 'short',
    });
    return `${datePart}, ${timePart}`;
  } catch {
    return iso;
  }
}
